/**
 * sfxPrefs — global sound effect mute toggle, persisted to localStorage.
 *
 * Apr 28 2026 Lead brief: chisel hammer + screen-rattle SFX should be on
 * by default but muteable from the episode footer button. We use a tiny
 * hand-rolled pub/sub instead of pulling in a state library so any
 * component (StoneTablet, ChainTimeline, EpisodeFrame, HUDLayout) can
 * subscribe and re-render when the user flips the mute.
 *
 * Storage key: `tsogn-sfx-muted`. Value: '1' = muted, '0' or absent = on.
 */
import { useEffect, useState } from 'react';

const KEY = 'tsogn-sfx-muted';
const listeners = new Set();

function readMuted() {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(KEY) === '1'; } catch { return false; }
}

function writeMuted(muted) {
  try { window.localStorage.setItem(KEY, muted ? '1' : '0'); } catch { /* ignore */ }
}

export function isSfxMuted() {
  return readMuted();
}

export function setSfxMuted(muted) {
  const next = !!muted;
  writeMuted(next);
  listeners.forEach((fn) => { try { fn(next); } catch { /* ignore */ } });
}

export function toggleSfxMuted() {
  const next = !readMuted();
  setSfxMuted(next);
  return next;
}

/**
 * useSfxMuted — hook that reads + subscribes to the global mute state.
 * Returns [muted, setMuted].
 */
export function useSfxMuted() {
  const [muted, setMuted] = useState(readMuted);
  useEffect(() => {
    const fn = (next) => setMuted(next);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return [muted, setSfxMuted];
}
